import React, { useEffect, useState, Suspense, lazy } from 'react';
import { Hero } from './components/Hero';
import { CountdownBanner } from './components/CountdownBanner';
import { initMetaPixel, trackPageView, trackViewContent } from './utils/metaPixel';
// Below-the-fold sections are code-split so the initial bundle only
// contains what's needed to paint the hero. Each chunk loads on demand
// as the user scrolls.
const Hook = lazy(() => import('./components/Hook').then(m => ({
  default: m.Hook
})));
const Pain = lazy(() => import('./components/Pain').then(m => ({
  default: m.Pain
})));
const Revelation = lazy(() => import('./components/Revelation').then(m => ({
  default: m.Revelation
})));
const Authority = lazy(() => import('./components/Authority').then(m => ({
  default: m.Authority
})));
const Testimonials = lazy(() => import('./components/Testimonials').then(m => ({
  default: m.Testimonials
})));
const Offer = lazy(() => import('./components/Offer').then(m => ({
  default: m.Offer
})));
const FAQ = lazy(() => import('./components/FAQ').then(m => ({
  default: m.FAQ
})));
const Guarantee = lazy(() => import('./components/Guarantee').then(m => ({
  default: m.Guarantee
})));
const FinalCTA = lazy(() => import('./components/FinalCTA').then(m => ({
  default: m.FinalCTA
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const PurchaseNotifications = lazy(() => import('./components/PurchaseNotifications').then(m => ({
  default: m.PurchaseNotifications
})));
// Minimal placeholder reserving vertical space so lazy-loaded sections
// don't cause layout shift while their chunk is being fetched.
function SectionPlaceholder({
  className = ''
}: {
  className?: string;
}) {
  return <div className={`w-full ${className}`} aria-hidden="true" data-id="element-0" />;
}
export function App() {
  // Hold off mounting below-the-fold sections until the hero has painted.
  // This frees the main thread to render + decode the LCP image first.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    // Schedule on idle so the browser paints the hero first, then loads
    // the rest. Fall back to a small timeout in browsers without rIC.
    const ric = (window as any).requestIdleCallback as ((cb: () => void, opts?: {
      timeout: number;
    }) => number) | undefined;
    const handle = ric ? ric(() => setReady(true), {
      timeout: 1500
    }) : window.setTimeout(() => setReady(true), 600) as unknown as number;
    initMetaPixel();
    trackPageView();
    trackViewContent();
    return () => {
      if (ric && typeof (window as any).cancelIdleCallback === 'function') {
        ;
        (window as any).cancelIdleCallback(handle);
      } else {
        window.clearTimeout(handle as number);
      }
    };
  }, []);
  return <main className="min-h-screen bg-cream" data-id="element-1">
      <CountdownBanner data-id="element-2" />
      <noscript data-id="element-3">
        <img height="1" width="1" style={{
        display: 'none'
      }} src="https://www.facebook.com/tr?id=1299609178999376&ev=PageView&noscript=1" alt="" data-id="element-4" />
      </noscript>
      <Hero data-id="element-5" />

      {ready && <Suspense fallback={<SectionPlaceholder className="h-32" data-id="element-7" />} data-id="element-6">
          <Hook data-id="element-8" />
          <Pain data-id="element-9" />
          <Revelation data-id="element-10" />
          <Authority data-id="element-11" />
          <Testimonials data-id="element-12" />
          <Offer data-id="element-13" />
          <FAQ data-id="element-14" />
          <Guarantee data-id="element-15" />
          <FinalCTA data-id="element-16" />
          <Footer data-id="element-17" />
          <PurchaseNotifications data-id="element-18" />
        </Suspense>}
    </main>;
}